/* eslint-disable */
import React, { FC, ReactElement, useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import { darken } from '@mui/material';
import { clsx } from 'clsx';
// Components
import MapView from '../../../components/map/bookingMap';
import LoaderFullPage from '../../../components/loaderFullPage';
// Modules
import { actions as currentBookingActions } from '../../../core/modules/currentBooking';
import { actions as modalActions, ActionOpenModal, ModalType } from '../../../core/modules/modals';
import { PaymentClearanceState } from '../../../components/modals/paymentClearance';
import { ModalSize } from '../../../components/modals/createModalHOC';
// Queries
import { useGetSpecificRide } from '../../../core/services/rides';
// Resources
import { client } from '../../../core/services/helpers';
import { Ride, Status, ServiceStatus } from '../../../core/apiTypes';
import { paths } from '../paths';
import { stringsRes } from '../../../resources/strings';
import { DELAY_SET_NO_DRIVER_AVAIALBLE, TRACKING_PULL_TIME } from '../../../utils/constants';
import {
    getCancellableStatus,
    getEncodedRoute,
    getFinishedAndCancelledStatus,
    isBookingCancelled,
    isBookingFinishedNotCanceled,
    isClearanceBlocked,
    isRideCancelledForPayment,
} from '../../../utils/business';
import { prepareWaypoints } from '../../../utils/map';
import { getUser } from '../../../localStorageAPI';
import LCEvent from '../../../utils/customEvents';
import ReservationDetails from '../reservations/reservationDetails';
import { useBreakpoints, WebLocation } from '../../../customHooks/useBreakpoints';

interface BookingTrackingMobileProps {
    resetAll: () => any;
    openModal: ActionOpenModal;
}

const BookingTrackingMobile: FC<BookingTrackingMobileProps> = (props): ReactElement => {
    const { resetAll, openModal } = props;
    const { isMobileSize } = useBreakpoints(WebLocation.FLOW);
    const classes = useStyles({ isMobile: isMobileSize });
    const navigate = useNavigate();
    const user = getUser();

    //= ======================================//
    //= ============== Queries ===============//
    //= ======================================//

    const { id } = useParams();
    const {
        data: ride,
        refetch,
        isLoading,
    } = useGetSpecificRide<Ride>({
        enabled: !!id,
        variables: { id },
        refetchInterval: TRACKING_PULL_TIME,
        onSuccess: (data: any) => {
            if (data) {
                console.log(data);
                setStatus(data?.status);
                client.invalidateQueries('historySoon');
            }
        },
    });

    //= ========================================//
    //= ============== Variables ===============//
    //= ========================================//

    const [status, setStatus] = useState<Status>(Status.PROCESSING);

    // LT-1884 niveau 3 — le detail sur CETTE course. Le niveau 1 (alerte
    // d'ensemble) et le niveau 2 (badge dans la liste) vivent ailleurs ; ici on
    // est sur une course precise, donc on peut nommer son etat exact.
    //
    // Le verdict est relu a chaque tour de sondage, mais la modale ne s'ouvre
    // qu'une fois par ETAT : sans cela elle se rouvrirait toutes les 10
    // secondes sous les doigts du passager. Un passage de refuse a annule
    // rouvre en revanche, parce que c'est une nouvelle information.
    const shownClearanceState = useRef<PaymentClearanceState | null>(null);
    useEffect(() => {
        if (!ride) return;
        let state: PaymentClearanceState | null = null;
        if (isRideCancelledForPayment(ride)) {
            state = PaymentClearanceState.CANCELLED;
        } else if (isClearanceBlocked(ride)) {
            state = PaymentClearanceState.BLOCKED;
        }
        if (!state || shownClearanceState.current === state) return;
        shownClearanceState.current = state;
        openModal(ModalType.PAYMENT_CLEARANCE, {
            modalSize: ModalSize.sm,
            title:
                state === PaymentClearanceState.CANCELLED
                    ? stringsRes.paymentClearance.cancelledTitle
                    : stringsRes.paymentClearance.blockedTitle,
            state,
            onChangePaymentMethod: () => navigate(paths.payment),
            onBookAgain: () => navigate(paths.home),
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ride?.payment_clearance_status, ride?.status]);
    const [isNoDriverAvailable, setIsNoDriverAvailable] = useState<boolean>(
        ride?.service?.status === ServiceStatus.NO_DRIVER_AVAILABLE,
    );

    const { stops } = ride || {};
    const isCancelled = isBookingCancelled(status);
    const isCancellable = getCancellableStatus().includes(status);
    const isFinishedOrCanceled = getFinishedAndCancelledStatus().includes(status);
    // const isTimeOut = status === Status.DISPATCH_TIMEOUT;

    //= ========================================//
    //= ============== UseEffect ===============//
    //= ========================================//

    useEffect(() => {
        setTimeout(() => {
            setIsNoDriverAvailable(false);
        }, DELAY_SET_NO_DRIVER_AVAIALBLE);
    }, []);

    // Request from cache
    useEffect(() => {
        if (ride) {
            setStatus(ride?.status);
        }
    }, [ride]);

    useEffect(() => {
        if (!id) {
            navigate(paths.home);
            return;
        }
        if (isBookingFinishedNotCanceled(status) && ride?.riders?.[0].id !== user?.id) {
            navigate(`${paths.tripHistory}/${ride.id}`);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [status, ride]);

    React.useEffect(() => {
        const handleForegroundChange = () => {
            console.log('Foreground Event catched', { isLoading });
            if (!isLoading) {
                console.log('Ask refetch ride');
                refetch();
            }
        };

        window.addEventListener(LCEvent.FOREGROUND, handleForegroundChange);
        window.addEventListener(LCEvent.NOTIFICATION, handleForegroundChange);
        return () => {
            window.removeEventListener(LCEvent.FOREGROUND, handleForegroundChange);
            window.removeEventListener(LCEvent.NOTIFICATION, handleForegroundChange);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoading]);

    //= ======================================//
    //= ============== Actions ===============//
    //= ======================================//

    // The tracking replaced the booking screen it was opened from, so there is nothing meaningful
    // left behind it: go home explicitly instead of popping the stack.
    const onBackToHome = (): void => {
        navigate(paths.home);
    };

    /* FOR DEBUG  - action to skip steps */
    // const onChangeStatus = (): void => {
    //     if (status === Status.PROCESSING) {
    //         setStatus(Status.ACCEPTED);
    //     }
    //     if (status === Status.ACCEPTED) {
    //         setStatus(Status.AT_PICKUP);
    //     }
    //     if (status === Status.AT_PICKUP) {
    //         setStatus(Status.PASSENGER_ON_BOARD);
    //     }
    //     if (status === Status.PASSENGER_ON_BOARD) {
    //         setStatus(Status.AT_DROP_OFF);
    //     }
    //     if (status === Status.AT_DROP_OFF) {
    //         setStatus(Status.FINISHED);
    //     }
    //     if (status === Status.FINISHED) {
    //         setStatus(Status.PROCESSING);
    //     }
    //     ride.status = status;
    // };

    /* FOR DEBUG  - change steps */
    // if (ride) {
    //     ride.status = Status.PASSENGER_ON_BOARD;
    //     ride.driver_tracking = { eta: 230 } as any;
    //     ride.driver = { firstname: 'elkzj', lastname: 'zallkazemla', phone_number: '0342344556' } as any;
    //     ride.vehicle = { registration_number: 'lkjez_231_lakej', model: 'CLASS S', brand: 'MERCEDES' } as any;
    // }

    //= ======================================//
    //= ============== Renders ===============//
    //= ======================================//

    console.log({ status, isCancelled, isCancellable });

    if (!ride) {
        return <LoaderFullPage color="secondary" />;
    }

    const driverInfosShowed = document.querySelector('.driverInfos') as any;
    const customDetailsStyle = isMobileSize && {
        top: `calc(100% - ${270 + (driverInfosShowed?.offsetHeight ? driverInfosShowed?.offsetHeight + 16 : 0)}px)`,
    };
    const customMapStyle = isMobileSize && {
        height: `calc(100% - ${270 + (driverInfosShowed?.offsetHeight ? driverInfosShowed?.offsetHeight + 16 : 0)}px)`,
    };

    return (
        <div className={classes.bookingTrackingMobile}>
            <MapView
                stops={stops}
                reservation={ride}
                waypoints={isBookingCancelled(status) ? [] : prepareWaypoints(getEncodedRoute(ride))}
                actionsCustom={{ zoomControl: false }}
                roundedTopBorder={false}
                refreshBounds={false}
                className={classes.mapNoLimit}
                style={{ ...customMapStyle }}
                backArrow
                onBack={onBackToHome}
                fullScreen
                showCurrentPositionPin={status !== Status.PASSENGER_ON_BOARD}
            />
            <div className={clsx(classes.bookingTrackingMobileMainContainer)} style={{ ...customDetailsStyle }}>
                <ReservationDetails preloadReservation={ride} />

                {/* OLD Version */}
                {/* <BackButton
                    label={
                        isFinishedOrCanceled
                            ? stringsRes.drawerMenu.home
                            : stringsRes.bookingSoon.status.inProgress.slice(0, -1)
                    }
                    spaceBottom={2}
                    backTo={paths.home}
                /> 
                <ReservationTracking reservation={ride} openModal={openModal}/> */}

                {/* FOR DEBUG  - button to skip steps */}
                {/* <Space value={2} />
                <RadiusButton title="DEBUG" onClick={onChangeStatus} className={classes.roundedButton} noSpace /> */}

                {/* <Space value={2} />
                <div className={classes.centerContainer}>
                    {!isCancelled && isCancellable && (
                        <RadiusButton
                            title={stringsRes.common.cancelRide}
                            onClick={onCancelReservation}
                            className={classes.cancelButton}
                        />
                    )}
                </div> */}
            </div>
        </div>
    );
};

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        mapNoLimit: {
            position: (props: { isMobile: boolean }) => (props.isMobile ? 'fixed' : 'relative'),
            width: '100%',
            height: '100vh',
            maxHeight: 'unset',
        },
        bookingTrackingMobile: {
            display: 'flex',
            flex: '100%',
            flexDirection: 'column',
            height: '100%',
            width: '100%',
            maxHeight: 'calc( 100% )',
        },
        bookingTrackingMobileMainContainer: (props: { isMobile: boolean }) => ({
            display: 'flex',
            flexDirection: 'column',

            // Mobile-specific styles
            ...(props.isMobile
                ? {
                      zIndex: 5,
                      justifyContent: 'flex-end',
                      position: 'absolute',
                      width: '100%',
                  }
                : {}),

            '& > div[class*=contentContainerRoot]': {
                marginTop: 'unset!important',
                backgroundColor: props.isMobile ? theme.palette.background.default : 'transparent',
                '& div:first-child': {
                    marginTop: 'unset!important',
                },
            },
            '& > div [class*="underMapContainerReservationCard"]': {
                padding: 20,
            },
        }),
        centerContainer: {
            alignSelf: 'center',
        },
        cancelButton: {
            padding: '7px 20px',
            borderColor: theme.palette.colors.text.error,
            borderRadius: 10,
            borderWidth: 2,
            background: theme.palette.background.default,
            color: theme.palette.colors.text.error,
            fontWeight: 800,
            fontFamily: 'Madras',
            '&:hover': {
                borderColor: theme.palette.colors.text.error,
                background: theme.palette.background.default,
            },
        },
        roundedButton: {
            padding: 14,
            minWidth: 'unset',
            backgroundColor: theme.palette.colors.border,
            border: 'unset',
            '&:hover': {
                backgroundColor: darken(theme.palette.colors.border, 0.05),
                border: 'unset',
            },
        },
    }),
);

const mapStateToProps = () => ({});

const mapDispatchToProps = {
    resetAll: currentBookingActions.resetAll,
    openModal: modalActions.openModal,
};

export default connect(mapStateToProps, mapDispatchToProps)(BookingTrackingMobile);
